/**
 * Whether the UI offers to START a timer, and what a timer control should show.
 *
 * The timer is withdrawn from the interface as of 2026-08-31 (HW-871, agreed
 * with Kateryna) — a frontend-only withdrawal, with the endpoints untouched.
 * Every open defect in the timer is reachable only through its two start
 * controls, and each of them costs real hours: a timer started as "No project —
 * general time" runs and then discards the time in silence (HW-837/HW-844), a
 * failed start or stop leaves the clock ticking a timer that no longer exists
 * (HW-836), and a timer that crosses a month boundary now correctly refuses to
 * write at all (HW-861). Manual entry — "Log Time" — is unaffected and is the
 * supported way to record hours meanwhile.
 *
 * One flag, in one module, read by both surfaces, so they cannot drift apart:
 * hiding the button on one screen and leaving the other is exactly the failure
 * this file exists to prevent. Flip it back to `false` when the flow redesign
 * lands (HW-823/HW-824) and the start path is worth offering again.
 */
export const TIMER_START_HIDDEN = true;

/** What a start/stop control should render. */
export type TimerControl = 'stop' | 'start' | 'none';

/**
 * Stop is NEVER hidden — that is the whole shape of this rule.
 *
 * Timers are running right now, and the server keeps counting one until
 * something stops it. A hidden Stop would leave those ticking with no way to
 * close them from the UI, so the withdrawal removes the way *in* and never the
 * way *out*.
 *
 * `startHidden` is a parameter rather than a direct read of the constant so the
 * rule can be exercised in both states without touching module state.
 */
export function timerControl(
  isRunning: boolean,
  startHidden: boolean = TIMER_START_HIDDEN,
): TimerControl {
  if (isRunning) return 'stop';
  return startHidden ? 'none' : 'start';
}
